import {
  BACKBLAZE_PRICE_CHECKED,
  BACKBLAZE_PRICE_PER_TB_MONTH,
  type HyperscalerProvider,
  formatDollars,
  storageTierChoice,
  storageTierLine,
  vaultProviderName,
} from "@bigbear/shared";

/**
 * Provider pricing, for the worked example on the Providers page. Numbers are
 * each provider's own published list price, not a negotiated or promotional
 * rate, and they move — a business whose margin depends on a fraction of a
 * cent per gigabyte updates these often. Treat every figure here as a rough
 * guide to the shape of the bill, not a quote, and check the provider's own
 * pricing page before deciding.
 *
 * Backblaze's own figure reads the shared `@bigbear/shared` constant, the same
 * one the desktop wizard's cost estimate uses, so the two cannot drift apart.
 * The Amazon S3, Google Cloud and Azure rows read the shared hyperscaler price
 * table the same way. Each of those rows carries its own `checked` date from
 * the shared side — bumping a shared price must not re-date the prices nobody
 * re-checked.
 */

/** When the prices below were last checked, for every row that does not carry
 * a `checked` date of its own. */
export const PRICES_CHECKED = "September 2026";

export interface ProviderPrice {
  id: string;
  provider: string;
  className: "sync" | "object-store" | "hyperscaler";
  /** One line: the plan or tier this price is for. */
  plan: string;
  /** Dollars per TB per month, storage only, rounded to the dollar except
   * where a provider prices in cents and rounding would misstate it —
   * Backblaze at $6.95 and the archive classes under a dollar or two today.
   * Backblaze's and the hyperscalers' read the shared tables, so they match
   * the desktop exactly. */
  storagePerTbMonth: number;
  /** What downloading data back out costs, in words — this is the term that
   * actually varies between the three classes, so it is prose rather than a
   * single number. */
  egress: string;
  /** Anything that changes the shape of the bill: minimums, per-account
   * caps, retrieval delay. */
  notes: string;
  source: string;
  /** When this row's price was last checked, when it is checked on its own
   * schedule rather than with the table: the rows read from `@bigbear/shared`,
   * which carry the shared side's date. */
  checked?: string;
}

/** One storage tier of the shared hyperscaler table, which must exist. */
function hyperscalerChoice(provider: HyperscalerProvider, requestValue: string) {
  const choice = storageTierChoice(provider, requestValue);
  if (!choice) throw new Error(`No ${provider} storage tier "${requestValue}".`);
  return choice;
}

/**
 * A row of the table below for one Amazon S3, Google Cloud or Azure class,
 * every figure in it read from the shared hyperscaler price table and every
 * fact from the storage class catalogue, so this page writes none of them
 * down.
 */
function hyperscalerRow(id: string, provider: HyperscalerProvider, requestValue: string): ProviderPrice {
  const { storageClass, price } = hyperscalerChoice(provider, requestValue);
  const retrieval = price.retrievalPerGb > 0 ? `, plus ${formatDollars(price.retrievalPerGb)}/GB to retrieve` : "";
  const wait = storageClass.retrieval === "delayed" ? `, and a wait of ${price.timeToFirstByte.toLowerCase()} first` : "";
  return {
    id,
    provider: vaultProviderName(provider),
    className: "hyperscaler",
    plan: storageClass.displayName,
    storagePerTbMonth: price.pricePerTbMonth,
    egress: `First ${price.freeEgressGbPerMonth} GB/mo free, then ${formatDollars(price.egressPerTb)}/TB${retrieval}${wait}`,
    notes:
      storageClass.retentionFloorDays === null
        ? "No minimum storage duration; ingress is always free, egress is the cost that matters here"
        : `${storageClass.retentionFloorDays}-day minimum storage duration; an early delete is billed the remainder`,
    source: price.pricingPage,
    checked: price.checked,
  };
}

/**
 * One row per plan actually worth comparing. Sync providers are priced per
 * account at a fixed tier rather than per TB, so `storagePerTbMonth` for
 * those rows is that tier's price divided by its capacity, to make the
 * per-TB comparison possible at all — the plan column carries the real
 * quote.
 */
export const PROVIDER_PRICES: ProviderPrice[] = [
  {
    id: "google-one-2tb",
    provider: "Google One",
    className: "sync",
    plan: "2 TB, $9.99/mo (or $99.99/yr)",
    storagePerTbMonth: 5,
    egress: "Free — downloads count against nothing",
    notes: "750 GB/day upload cap per account regardless of thread count",
    source: "one.google.com/about/plans",
  },
  {
    id: "dropbox-plus-2tb",
    provider: "Dropbox",
    className: "sync",
    plan: "Plus, 2 TB, $11.99/mo (or ~$9.99/mo billed yearly)",
    storagePerTbMonth: 6,
    egress: "Free",
    notes: "Writes to one folder serialise on Dropbox's side; reads do not",
    source: "dropbox.com/plans",
  },
  {
    id: "onedrive-1tb",
    provider: "Microsoft 365",
    className: "sync",
    plan: "Personal, 1 TB, $6.99/mo (or $69.99/yr)",
    storagePerTbMonth: 7,
    egress: "Free",
    notes: "Graph API throttles per app and tenant; guidance is to run few streams",
    source: "microsoft.com/microsoft-365/buy",
  },
  {
    id: "backblaze-b2",
    provider: "Backblaze B2",
    className: "object-store",
    plan: "Pay-as-you-go",
    storagePerTbMonth: BACKBLAZE_PRICE_PER_TB_MONTH,
    egress: "Free up to 3× what you store each month; $0.01/GB beyond that",
    notes: "No minimum retention or account size; a natural default for a large vault",
    source: "backblaze.com/cloud-storage/pricing",
    checked: BACKBLAZE_PRICE_CHECKED,
  },
  {
    id: "wasabi",
    provider: "Wasabi",
    className: "object-store",
    plan: "Pay-as-you-go",
    storagePerTbMonth: 7,
    egress: "Free (subject to a reasonable-use policy)",
    notes: "1 TB minimum billed capacity and a 90-day minimum retention per object",
    source: "wasabi.com/cloud-storage-pricing",
  },
  hyperscalerRow("s3-standard", "aws", "STANDARD"),
  hyperscalerRow("s3-deep-archive", "aws", "DEEP_ARCHIVE"),
  hyperscalerRow("gcs-archive", "gcs", "ARCHIVE"),
  hyperscalerRow("azure-archive", "azure", "Archive"),
];

export interface WorkedExample {
  id: string;
  label: string;
  /** What a year of storing 1 TB, then restoring all of it once, costs — as a
   * rounded whole-dollar range, since retrieval and egress pricing has steps
   * this simple model does not capture exactly. */
  costLabel: string;
}

/** A year of storing the example's 1 TB, to the whole dollar. */
export function yearOfOneTb(pricePerTbMonth: number): number {
  return Math.round(pricePerTbMonth * 12);
}

/** The same, for a row of the table above. Every storage figure below goes
 * through here rather than being written down, so editing a price — or the
 * shared Backblaze constant behind one — moves the worked example with it. */
function storageYear(priceId: string): number {
  const price = PROVIDER_PRICES.find((p) => p.id === priceId);
  if (!price) throw new Error(`No provider price with id "${priceId}".`);
  return yearOfOneTb(price.storagePerTbMonth);
}

/**
 * "Store 1 TB for a year, then restore it once" on one hyperscaler storage
 * tier: its year of storage from the price table, and its cost to restore
 * 1 TB — retrieval fee, the retrieved copy kept where reads wait on a
 * retrieval, and egress — from the shared storage tier choices.
 * The total adds the two rounded figures the label shows, so it adds up.
 */
function hyperscalerExample(id: string, provider: HyperscalerProvider, requestValue: string): WorkedExample {
  const { storageClass, price, restoreOneTb } = hyperscalerChoice(provider, requestValue);
  const storage = yearOfOneTb(price.pricePerTbMonth);
  const restore = Math.round(restoreOneTb);
  return {
    id,
    label: storageTierLine({ provider, storageClass: storageClass.requestValue }),
    costLabel: `~$${storage} storage + ~$${restore} to restore ≈ $${storage + restore}`,
  };
}

/** The 2 TB sync plans, both prorated to the 1 TB this example stores, so the
 * two rows compare on the same basis. Neither quotes the whole plan; the plan
 * column above carries the real subscription price. */
const googleOne = storageYear("google-one-2tb");
const dropbox = storageYear("dropbox-plus-2tb");

/**
 * "Store 1 TB for a year, then restore it once" — the comparison that
 * actually separates the three classes, because storage price alone makes
 * the hyperscalers look cheap and hides where the bill really comes from.
 */
export const WORKED_EXAMPLES: WorkedExample[] = [
  {
    id: "google-one",
    label: "Google One (2 TB plan, prorated)",
    costLabel: `~$${googleOne} (the subscription; the restore is free)`,
  },
  {
    id: "dropbox",
    label: "Dropbox Plus (2 TB plan, prorated)",
    costLabel: `~$${dropbox} (the subscription; the restore is free)`,
  },
  {
    id: "b2",
    label: "Backblaze B2",
    costLabel: `~$${storageYear("backblaze-b2")} storage, restore free (under the 3× allowance)`,
  },
  {
    id: "wasabi",
    label: "Wasabi",
    costLabel: `~$${storageYear("wasabi")} storage, restore free`,
  },
  hyperscalerExample("s3-standard", "aws", "STANDARD"),
  hyperscalerExample("s3-deep-archive", "aws", "DEEP_ARCHIVE"),
  hyperscalerExample("gcs-standard", "gcs", "STANDARD"),
  hyperscalerExample("gcs-archive", "gcs", "ARCHIVE"),
  hyperscalerExample("azure-hot", "azure", "Hot"),
  hyperscalerExample("azure-archive", "azure", "Archive"),
];
